import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CaseCommunicationDrawer } from '@core/models/case-communication/case-communication-drawer.model';
import { LayoutService } from '@core/services';
import { CaseCommunicationService } from '@core/services/case-communication.service';
import { CaseCommunicationStoreService } from '@core/services/case-communication.store.service';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { PendingActionsAlertComponent } from '@modules/dialogs/pending-actions-alert/pending-actions-alert.component';
import { SendNewEmailComponent } from '@modules/dialogs/send-new-email/send-new-email.component';
import { ViewEmailComponent } from '@modules/dialogs/view-email/view-email.component';
import { indicate } from '@shared/helpers';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-case-communication-drawer',
    templateUrl: './case-communication-drawer.component.html',
    styleUrls: ['./case-communication-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CaseCommunicationDrawerComponent implements OnInit {
  static readonly componentName = 'CaseCommunicationDrawerComponent';
  
  @Input() caseComData: CaseCommunicationDrawer;
  @Input() caseId: number;
  @Output() closeCaseCom = new EventEmitter<void>();

  public isLoadingSendEmailDialog$: Subject<boolean> = new Subject<boolean>();
  public showCaseEmailIdLoading$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  public sysText: any

  private destroy$: Subject<void> = new Subject<void>();

  get isMobile$(): Observable<boolean> {
    return this.layoutService.isMobile$();
  }

  constructor(
    private layoutService: LayoutService,
    private dialog: MatDialog,
    private service: CaseCommunicationService,
    private ccStore: CaseCommunicationStoreService,
    private laStore: LeaveAdminStoreService
  ) { }

  ngOnInit(): void {
    this.sysText = this.ccStore.sysText.caseCommunicationDrawer;
    this.ccStore.caseComDrawer$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => { this.caseComData = res });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.ccStore.unloadStore();
  }

  public onCloseCaseCom(): void {
    this.closeCaseCom.emit();
  }

  public onSendEmail(): void {
    if (this.laStore.isActionRunning) {
      this.openPendingChangesDialog();
      return;
    }

    this.service.getSendNewEmailDialog(this.caseId)
      .pipe(indicate(this.isLoadingSendEmailDialog$))
      .subscribe((res) => {
        const dialogConfig: MatDialogConfig = {
          id: SendNewEmailComponent.componentName,
          width: '600px',
          data: { 
            sysText: this.ccStore.sysText.sendNewEmailDialog,
            viewEmailSysText: this.ccStore.sysText.viewEmailDialog,
            unsavedSysText: this.ccStore.sysText.unsavedChanges,
            dialogData: res
          }
        };

        this.dialog.open(SendNewEmailComponent, dialogConfig)
          .afterClosed().subscribe(() => {
            this.ccStore.setCcIsEditing = false;
          });
      });
  }

  public onEmailClick(caseEmailId: number): void {
    this.showCaseEmailIdLoading$.next(caseEmailId);
    this.service.getEmailInfoDialog(caseEmailId)
      .pipe(finalize(() => this.showCaseEmailIdLoading$.next(null)))
      .subscribe((res) => {
        const dialogConfig: MatDialogConfig = {
          id: ViewEmailComponent.componentName,
          width: '750px',
          disableClose: false,
          data: { 
            sysText: this.ccStore.sysText.viewEmailDialog,
            emailData: res,
            isPreview: false
          }
        }
        
        this.dialog.open(ViewEmailComponent, dialogConfig);
      });
  }

  private openPendingChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px'
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.onSendEmail();
      }
    });
  }
}
